import React, { Component } from 'react'
import { Markdown } from './Markdown.js'
import { SimpleButton, SimpleIcon } from './Buttons.js'
import UserSaid from '../../assets/Icons/UserSaid.svg'
import AISaid from '../../assets/Icons/AISaid.svg'
import ToolServer from '../../assets/Icons/ToolServer.svg'
import Left from '../../assets/Icons/Back.svg'
import Right from '../../assets/Icons/Forward.svg'
import Up from '../../assets/Icons/Up.svg'
import Down from '../../assets/Icons/Down.svg'
import './ConversationSearchResult.css'

// Renders the outputs of the Conversation async toolset
// (Desktop/AsyncTools/Conversation, cleaned by
// Shared/conversation-tool-result.js):
//
//   conversation_search / tool_result_search
//     { conversation_id, query?, out_of?, hits: [node] }
//   conversation_browse
//     { conversation_id, node, parent?, siblings: [node], children: [node] }
//   tool_result_browse
//     { conversation_id, node, parent?, siblings: [node], children: [node] }
//   conversation_open_link / tool_result_open_link
//     { conversation_id, node }        node may carry children
//   conversation_index_status / tool_result_status
//     { conversation_id, state, error_message?, message_count?, root? }
//
// A node is { link?, content?, is_verbatim?, zoom?, exact boundary fields,
// child_count?, full_token_count?, children? }. A zoom-0 leaf's content is
// the pretty-printed JSON of one chat message ({ role, content, ... }),
// possibly cut off at the token budget; a zoom>0 node's content is summary
// prose. Search hit content is a snippet of the same text.

const TITLE_MAX = 70

const roleLabels = {
  user: 'You',
  assistant: 'Assistant',
  system: 'System',
  tool: 'Tool result'
}

const isObject = value => value && typeof value === 'object' && !Array.isArray(value)

const linkNodeHint = href => {
  let decoded = String(href || '')
  try {
    decoded = decodeURIComponent(decoded)
  } catch (err) {}
  if (decoded.startsWith('conversation://')) {
    if (decoded.includes('/mip/')) return { link: href, kind: 'summary_span', zoom: 1, child_count: 1 }
    if (decoded.includes('/transcript/')) return { link: href, kind: 'transcript_event', zoom: 0, child_count: 0 }
    return { link: href, kind: 'conversation', zoom: 1, child_count: 1 }
  }
  return { link: href }
}

// Markdown inside summaries and stored compaction records can itself contain
// opaque conversation links. Route those through the same onOpen callback as
// navigation rows instead of handing the custom scheme to the operating
// system. Non-conversation links keep the caller's normal Markdown renderer.
const componentsWithConversationLinks = (components, onOpen) => {
  if (!onOpen) return components
  const BaseAnchor = components && components.a
  return {
    ...(components || {}),
    a: props => {
      const href = props && props.node && props.node.properties && props.node.properties.href || props.href
      const opaque = typeof href === 'string' && (
        href.startsWith('conversation://') || href.startsWith('tool-result://')
      )
      if (!opaque) {
        return BaseAnchor ? <BaseAnchor {...props}/> : <a href={href}>{props.children}</a>
      }
      const open = event => {
        event.preventDefault()
        event.stopPropagation()
        onOpen(href, linkNodeHint(href))
      }
      return <a className='convOpaqueLink' href={href} onClick={open}>{props.children}</a>
    }
  }
}

const formatTokens = value => {
  const n = Number(value)
  if (!Number.isFinite(n)) return null
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`
  if (n >= 10000) return `${Math.round(n / 1000)}k`
  if (n >= 1000) return `${(n / 1000).toFixed(1)}k`
  return String(n)
}

// Message content can be a string or an array of content blocks
const contentToText = content => {
  if (typeof content === 'string') return content
  if (Array.isArray(content)) {
    return content
      .map(block => (typeof block === 'string' ? block : block.text || ''))
      .filter(Boolean)
      .join('\n')
  }
  if (isObject(content)) return content.text || ''
  return ''
}

// Decode the prefix of a JSON string literal (after its opening quote) up to
// its closing quote or the end of a truncated buffer.
const decodeJsonStringPrefix = value => {
  let result = ''
  let escaped = false
  for (let i = 0; i < value.length; i += 1) {
    const ch = value[i]
    if (escaped) {
      if (ch === 'n') result += '\n'
      else if (ch === 'r') result += '\r'
      else if (ch === 't') result += '\t'
      else if (ch === 'u' && i + 4 < value.length) {
        const code = parseInt(value.slice(i + 1, i + 5), 16)
        if (Number.isFinite(code)) {
          result += String.fromCharCode(code)
          i += 4
        } else {
          result += ch
        }
      } else {
        result += ch
      }
      escaped = false
    } else if (ch === '\\') {
      escaped = true
    } else if (ch === '"') {
      break
    } else {
      result += ch
    }
  }
  return result
}

// Decode JSON string escapes in place (single pass, handles \uXXXX).
const decodeJsonEscapes = value => value.replace(/\\(u[0-9a-fA-F]{4}|.)/g, (m, esc) => {
  if (esc[0] === 'u') return String.fromCharCode(parseInt(esc.slice(1), 16))
  const map = { n: '\n', t: '\t', r: '\r', '"': '"', '\\': '\\', '/': '/', b: '\b', f: '\f' }
  return map[esc] !== undefined ? map[esc] : m
})

const looksJsonEscaped = value => /\\[ntr"\\/]|\\u[0-9a-fA-F]{4}/.test(value)

// Recover a chat message from node content. Handles the intact object, the
// full JSON text, and JSON truncated mid-string by the token budget; search
// snippets that carry a fragment of the JSON are handled by the caller's
// fallback text path.
export const messageFromNodeContent = content => {
  if (isObject(content) && content.role) {
    return { role: content.role, text: contentToText(content.content), toolCalls: content.tool_calls }
  }
  if (typeof content !== 'string') return null
  const text = content.trim()
  if (!text.startsWith('{')) return null
  try {
    const parsed = JSON.parse(text)
    if (isObject(parsed) && parsed.role) {
      return { role: parsed.role, text: contentToText(parsed.content), toolCalls: parsed.tool_calls }
    }
  } catch (err) {
    // fall through to prefix extraction for budget-truncated JSON
  }
  const roleMatch = /"role"\s*:\s*"(\w+)"/.exec(text)
  const contentMatch = /"content"\s*:\s*"/.exec(text)
  if (!roleMatch && !contentMatch) return null
  return {
    role: roleMatch ? roleMatch[1] : undefined,
    text: contentMatch
      ? decodeJsonStringPrefix(text.slice(contentMatch.index + contentMatch[0].length))
      : '',
    truncated: true
  }
}

// The index also supplies role/kind as first-class node metadata. Search
// excerpts are deliberately not complete JSON records, so presentation must
// not depend on recovering a role from their text.
const messageFromNode = node => {
  const parsed = messageFromNodeContent(node && node.content)
  if (parsed) return parsed
  const role = node && (node.role || (
    node.kind === 'tool_result' ? 'tool' :
      /^user_/.test(node.kind || '') ? 'user' :
        /^assistant_/.test(node.kind || '') || node.kind === 'tool_call' ? 'assistant' : undefined
  ))
  return role ? { role, text: nodeDisplayText(node) } : null
}

// Display text for any node: the chat message body for a leaf, the summary
// prose for a summary node, and a de-JSON-ed best effort for snippets.
const nodeDisplayText = node => {
  const message = messageFromNodeContent(node.content)
  if (message && message.text) return message.text
  let text = typeof node.content === 'string' ? node.content : contentToText(node.content)
  // Search snippets are fragments of the leaf's JSON text; strip the
  // scaffolding when the fragment includes the content field marker.
  const marker = /"content"\s*:\s*"/.exec(text)
  if (marker) {
    text = decodeJsonStringPrefix(text.slice(marker.index + marker[0].length))
  } else {
    // A fragment from the middle of a JSON string still carries its escapes
    // (possibly doubled when the message itself embeds encoded text).
    for (let pass = 0; pass < 2 && looksJsonEscaped(text); pass += 1) {
      text = decodeJsonEscapes(text)
    }
  }
  return text
}

const nodeRole = node => {
  const message = messageFromNode(node)
  return message && message.role
}

const isSummaryNode = node => Number(node && node.zoom) > 0

const truncate = (text, max) => {
  if (text.length <= max) return text
  const cut = text.lastIndexOf(' ', max)
  return text.slice(0, cut > max * 0.6 ? cut : max) + ' ...'
}

const escapeRegExp = s => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

// Bold occurrences of query terms, like Google does in snippets
const highlightTerms = (text, query) => {
  const terms = (query || '').split(/\s+/).filter(t => t.length > 2)
  if (!terms.length) return text
  const re = new RegExp(`(${terms.map(escapeRegExp).join('|')})`, 'gi')
  // split with a capture group interleaves text and matches: odd indices are matches
  const parts = text.split(re)
  return parts.map((part, i) =>
    i % 2 === 1 ? <b key={i}>{part}</b> : part
  )
}

// conversation://open?conversation_id=X&handle=conversation/X/transcript/Y/...
// rendered as a breadcrumb the way Google renders a URL path
const linkToBreadcrumb = (link, conversationId) => {
  const crumbs = ['conversation']
  if (conversationId) crumbs.push(conversationId)
  try {
    const handle = new URL(link).searchParams.get('handle')
    if (handle) {
      const segments = decodeURIComponent(handle).split('/')
      const transcriptIdx = segments.indexOf('transcript')
      if (transcriptIdx >= 0 && segments[transcriptIdx + 1]) {
        crumbs.push('transcript', segments[transcriptIdx + 1])
      } else if (segments.includes('summary')) {
        crumbs.push(...segments.slice(segments.indexOf('summary')))
      }
    }
  } catch (err) {
    // not a parseable URL; breadcrumb falls back to conversation id
  }
  return crumbs
}

const nodeMeta = node => {
  const meta = []
  if (isSummaryNode(node)) {
    meta.push(`summary level ${node.zoom}`)
    if (node.child_count > 0) meta.push(`${node.child_count} ${node.child_count === 1 ? 'child' : 'children'}`)
    const tokens = formatTokens(node.full_token_count)
    if (tokens) meta.push(`summarizes ${tokens} tokens`)
  } else {
    const tokens = formatTokens(node.full_token_count)
    if (tokens) meta.push(`${tokens} tokens`)
    if (node.is_verbatim) meta.push('verbatim')
  }
  return meta
}

// One Google-style result row for a search hit (a cleaned node)
export const ConversationSearchResult = props => {
  const { result, query, conversationId, onOpen } = props
  const node = result.node || result
  const link = result.link || node.link
  const text = nodeDisplayText(node).replace(/\s+/g, ' ').trim()
  const role = nodeRole(node)
  const summary = isSummaryNode(node)
  const siteName = summary
    ? `Summary · level ${node.zoom}`
    : roleLabels[role] || role || 'Message'

  const title = node.title || truncate(text, TITLE_MAX) || siteName
  // Keep the exact Typesense excerpt in the DOM; presentation constrains it
  // to the compact multi-line shape of a search result.
  const snippet = text
  const crumbs = linkToBreadcrumb(link, result.conversation_id || conversationId)
  const meta = nodeMeta(node)

  const open = e => {
    e.preventDefault()
    if (onOpen) onOpen(link, result)
  }

  return (
    <div className='convSearchResult result-item'>
      <div className='convSearchResultBreadcrumb result-url'>
        <span>{siteName}</span>
        {crumbs.map((crumb, i) => (
          <span key={i}>
            <span className='convSearchResultCrumbSep'> › </span>
            {crumb}
          </span>
        ))}
      </div>
      <a className='convSearchResultTitle result-title' href={link} onClick={open}>
        {title}
      </a>
      <div className='convSearchResultSnippet result-snippet'>
        {meta.length > 0 && <span className='convSearchResultMeta'>{meta.join(' · ')} — </span>}
        {highlightTerms(snippet, query)}
      </div>
    </div>
  )
}

export const ConversationSearchResults = props => {
  const { results, query, conversationId, onOpen, nextStartAt, onMore } = props
  const hits = results || []
  if (!hits.length) {
    return <div className='convSearchResults convSearchEmpty'>No matches{query ? ` for “${query}”` : ''}</div>
  }
  return (
    <div className='convSearchResults google-results'>
      {hits.map((result, i) => (
        <ConversationSearchResult
          key={(result.node || result).link || i}
          result={result}
          query={query}
          conversationId={conversationId}
          onOpen={onOpen}
        />
      ))}
      {nextStartAt !== null && nextStartAt !== undefined && onMore &&
        <SimpleButton label='Load more' action={() => onMore(nextStartAt)}/>}
    </div>
  )
}

// A recovered transcript message rendered as a chat message
export const ConversationChatMessage = props => {
  const { node, components } = props
  const message = messageFromNode(node) || { text: nodeDisplayText(node) }
  const role = message.role || 'message'
  const label = roleLabels[role] || role
  const meta = nodeMeta(node)
  const toolCalls = Array.isArray(message.toolCalls) ? message.toolCalls : []
  const content = <React.Fragment>
    {message.text && <Markdown components={components}>{message.text}</Markdown>}
    {toolCalls.map((call, i) => {
      const fn = call.function || {}
      let args = fn.arguments || ''
      try {
        const parsed = JSON.parse(args)
        args = typeof parsed === 'string' ? parsed : JSON.stringify(parsed, null, 2)
      } catch (err) {}
      return <div key={i} className='toolUseDiv'>
        <div className='toolUseHeader'>
          <div className='toolUseHeaderLeft'>
            <div className='toolUserHeaderIcon'><SimpleIcon src={ToolServer}/></div>
            <div className='toolUseHeaderTitle'>{fn.name || call.id || 'Tool call'}</div>
          </div>
        </div>
        {args && <div className='toolUseContent'><pre>{args}</pre></div>}
      </div>
    })}
    {message.truncated && <div className='convTranscriptTruncated'>… cut off at the token budget</div>}
  </React.Fragment>

  if (role === 'user') {
    return <div className='convTranscriptNode keyboardEditInstruction aiUser aiUser-idle'>
      <div className='keyboardEditIconAndInstruction'>
        <div className='keyboardEditInstructionLeftIcon'><SimpleIcon src={UserSaid}/></div>
        <div className='keyboardEditInstructionText'><div className='nonEditing'>{content}</div></div>
        <div className='rightColumn1'/>
      </div>
    </div>
  }

  if (role === 'tool') {
    return <div className='convTranscriptNode toolUseDiv'>
      <div className='toolUseHeader'>
        <div className='toolUseHeaderLeft'>
          <div className='toolUserHeaderIcon'><SimpleIcon src={ToolServer}/></div>
          <div className='toolUseHeaderTitle'>{node.title || label}</div>
        </div>
        {meta.length > 0 && <div className='toolUseHeaderRight convTranscriptMeta'>{meta.join(' · ')}</div>}
      </div>
      <div className='toolUseContent'>{content}</div>
    </div>
  }

  return <div className='convTranscriptNode aiSaidMaybeCompacted'>
    <div className='horizontalTextLayout'>
      <div className='leftColumn1'>
        <div className='keyboardEditInstructionLeftIcon'><SimpleIcon src={AISaid}/></div>
      </div>
      <div className='middleColumn1'>
        <div className='chatGptChatMessageHeaderTopic2'>
          <span>{label}</span>
          {meta.length > 0 && <span className='convTranscriptMeta'>{meta.join(' · ')}</span>}
        </div>
        <div className='convTranscriptBody'>{content}</div>
      </div>
      <div className='rightColumn1'/>
    </div>
  </div>
}

// A summary node: its zoom, exact closed boundary, summarized token count,
// prose, and the same opaque navigation link exposed to the model.
export const ConversationSummaryNode = props => {
  const { node, components, onOpen } = props
  const text = nodeDisplayText(node)
  const meta = nodeMeta(node)
  const label = node && node.kind === 'tool_result_summary'
    ? 'Tool result summary'
    : 'Conversation summary'
  return <div className='convTranscriptNode convSummaryNode aiSaidMaybeCompacted'>
    <div className='horizontalTextLayout'>
      <div className='leftColumn1'>
        <div className='keyboardEditInstructionLeftIcon'><SimpleIcon src={AISaid}/></div>
      </div>
      <div className='middleColumn1'>
        <div className='chatGptChatMessageHeaderTopic2'>
          <span>{label}</span>
          <span className='convTranscriptMeta'>{meta.join(' · ')}</span>
        </div>
        {text && <div className='convSummaryNodeText'>
          <Markdown components={componentsWithConversationLinks(components, onOpen)}>{text}</Markdown>
        </div>}
      </div>
      <div className='rightColumn1'/>
    </div>
  </div>
}

// Any node, one level deep: chat message for a leaf, summary card otherwise
export const ConversationNode = props => {
  const { node, components, onOpen, renderLeaf } = props
  if (!node) return null
  if (Number(node.zoom) === 0) {
    if (renderLeaf) {
      const renderedLeaf = renderLeaf(node)
      if (renderedLeaf) return renderedLeaf
    }
    return <ConversationChatMessage node={node} components={components} onOpen={onOpen}/>
  }
  if (isSummaryNode(node) || !messageFromNode(node)) {
    return <ConversationSummaryNode node={node} components={components} onOpen={onOpen}/>
  }
  return <ConversationChatMessage node={node} components={components} onOpen={onOpen}/>
}

const relatedNodeLink = ({ label, relation, node, onOpen }) => {
  if (!node || !node.link) return null
  const open = event => {
    if (event) {
      event.preventDefault()
      event.stopPropagation()
    }
    if (onOpen) onOpen(node.link, node)
  }
  const icon = relation === 'previous' ? Left : relation === 'next' ? Right : relation === 'parent' ? Up : Down
  const buttonLabel = node.title ? <span>
    <span className='convNodeControlRelation'>{label}</span>
    <span className='convNodeControlTitle'>{node.title}</span>
  </span> : label
  return <div key={relation} className={`convNodeControl convNodeControl-${relation}`} title={node.title || node.link}>
    <SimpleButton icon={icon} label={buttonLabel} action={open}/>
  </div>
}

// These are direct projections of backend relationships, not a client-side
// hierarchy. Back/forward are the returned siblings, zoom out is the returned
// parent, and zoom in uses the returned first/last child links.
export const ConversationNodeNavigation = props => {
  const { parent, previous, next, children, onOpen } = props
  const childNodes = (children || []).filter(candidate => candidate && candidate.link)
  const firstChild = childNodes[0]
  const lastChild = childNodes.length > 1 ? childNodes[childNodes.length - 1] : null
  const controls = [
    relatedNodeLink({ label: 'Zoom out', relation: 'parent', node: parent, onOpen }),
    relatedNodeLink({ label: '← Previous sibling', relation: 'previous', node: previous, onOpen }),
    relatedNodeLink({ label: 'Next sibling →', relation: 'next', node: next, onOpen }),
    relatedNodeLink({
      label: lastChild ? 'Zoom in · first child' : 'Zoom in',
      relation: 'first-child',
      node: firstChild,
      onOpen
    }),
    relatedNodeLink({ label: 'Zoom in · last child', relation: 'last-child', node: lastChild, onOpen })
  ].filter(Boolean)
  if (!controls.length) return null
  return <nav className='convNodeControls' aria-label='Conversation node navigation'>{controls}</nav>
}

// conversation_browse/tool_result_browse: one node with explicit navigation
// links. Conversation browse relations are paged under the caller's budget.
export const ConversationBrowseResult = props => {
  const { result, components, onOpen, onMore, renderLeaf } = props
  const children = result.children || []
  return (
    <div className='convBrowse'>
      {result.node && <ConversationNode node={result.node} components={components} onOpen={onOpen} renderLeaf={renderLeaf}/>}
      {result.node && <ConversationNodeNavigation
        node={result.node}
        parent={result.parent}
        previous={result.previous}
        next={result.next}
        children={children}
        onOpen={onOpen}/>}
      {result.next_start_at !== null && result.next_start_at !== undefined && onMore &&
        <SimpleButton label='Load more' action={() => onMore(result.next_start_at)}/>}
    </div>
  )
}

// conversation_open_link: ground truth for one zoom-0 source event.
export const ConversationOpenLinkResult = props => {
  const { result, components, onOpen, onMore, renderLeaf } = props
  const node = result.node
  if (!node) return <div className='convSearchEmpty'>Nothing at that link</div>
  return (
    <div className='convBrowse'>
      <ConversationNode node={node} components={components} onOpen={onOpen} renderLeaf={renderLeaf}/>
      <ConversationNodeNavigation node={node} children={node.children || []} onOpen={onOpen}/>
      {result.next_start_at !== null && result.next_start_at !== undefined && onMore &&
        <SimpleButton label='Load more' action={() => onMore(result.next_start_at)}/>}
    </div>
  )
}

// --- summary spine ---------------------------------------------------------
// A compaction spine is a sequence of completed summary-node references. Each
// reference is resolved through conversation_browse and rendered by the same
// complete-node component used in the Conversation tab and tool results.
// Unsummarized source events are not compaction-spine entries.

const spineZoomFromHandle = handle => {
  const match = /\/mip\/level-(\d+)\//.exec(String(handle || '')) ||
    /\/mip(\d+)\//.exec(String(handle || ''))
  return match ? Number(match[1]) : undefined
}

export const isSummarySpineHandle = handle => String(handle || '').includes('/mip')

// Map a stored exact-boundary summary record
// onto the cleaned node shape every other view renders.
export const summaryRecordToNode = (record, handle) => {
  const source = record || {}
  const zoom = Number(source.summaryLevel)
  const node = {
    content: source.head || '',
    zoom: Number.isFinite(zoom) && zoom > 0 ? zoom : (spineZoomFromHandle(handle) || 1)
  }
  const childCount = Number(source.childCount)
  if (Number.isFinite(childCount) && childCount > 0) node.child_count = childCount
  const tokens = Number(source.fullTokenCount)
  if (Number.isFinite(tokens) && tokens > 0) node.full_token_count = tokens
  if (source.link) node.link = source.link
  return node
}

class ConversationSpineBrowseNode extends Component {
  state = { result: null, loading: false, error: null }

  componentDidMount() {
    void this.load()
  }

  componentDidUpdate(prevProps) {
    if (prevProps.link !== this.props.link || (!prevProps.loadBrowse && this.props.loadBrowse)) {
      void this.load()
    }
  }

  componentWillUnmount() {
    this.requestId = (this.requestId || 0) + 1
  }

  load = async () => {
    const { link, loadBrowse } = this.props
    if (!link || !loadBrowse) return
    const requestId = (this.requestId || 0) + 1
    this.requestId = requestId
    this.setState({ result: null, loading: true, error: null })
    try {
      const result = await loadBrowse(link)
      if (this.requestId !== requestId) return
      this.setState({ result, loading: false, error: null })
    } catch (err) {
      if (this.requestId !== requestId) return
      this.setState({ result: null, loading: false, error: err && err.message || String(err) })
    }
  }

  render() {
    const { fallbackNode, components, onOpen } = this.props
    if (this.state.result && this.state.result.node) {
      return <ConversationBrowseResult result={this.state.result} components={components} onOpen={onOpen}/>
    }
    if (this.state.error && fallbackNode && fallbackNode.content) {
      return <div>
               <ConversationNode node={{ ...fallbackNode, link: undefined }}
                                 components={components}
                                 onOpen={onOpen}/>
               <div className='convIndexStatusError'>
                 Current index navigation is unavailable for this historical summary.
               </div>
             </div>
    }
    if (this.state.error) return <div className='convIndexStatusError'>{this.state.error}</div>
    if (this.state.loading) return <div className='convSpinePending'>Loading conversation node…</div>
    if (fallbackNode && fallbackNode.content) {
      return <ConversationNode node={fallbackNode} components={components} onOpen={onOpen}/>
    }
    return <div className='convSpinePending'>Conversation node unavailable</div>
  }
}

export const ConversationSummarySpine = props => {
  const {
    handles,
    recordsByHandle,
    pendingText,
    components,
    onOpen,
    loadBrowse,
    storedContent,
    storedContentPending,
    exactContentUnavailable
  } = props
  const list = handles || []
  if (!list.length) {
    return <div className='convSpine convSearchEmpty'>{pendingText || 'No completed summary nodes'}</div>
  }
  const byHandle = recordsByHandle || {}
  const missingHistoricalText = list.some(handle => {
    const node = summaryRecordToNode(byHandle[handle] || {}, handle)
    return !String(node.content || '').trim()
  })
  return (
    <div className='convSpine'>
      {missingHistoricalText && storedContent &&
        <ConversationSummaryNode
          node={{
            kind: 'summary_span',
            zoom: 1,
            content: storedContent
          }}
          components={components}
          onOpen={onOpen}/>}
      {missingHistoricalText && !storedContent && storedContentPending &&
        <div className='convSpinePending'>Loading the exact historical compaction input…</div>}
      {missingHistoricalText && !storedContent && exactContentUnavailable &&
        <div className='convIndexStatusError'>
          Exact historical compaction text is unavailable; refresh this compaction before relying on its spine.
        </div>}
      {list.map((handle, i) => {
        const record = byHandle[handle] || {}
        const fallbackNode = summaryRecordToNode(record, handle)
        return <ConversationSpineBrowseNode
                 key={handle || i}
                 link={record.link || fallbackNode.link}
                 fallbackNode={fallbackNode}
                 loadBrowse={loadBrowse}
                 components={components}
                 onOpen={onOpen}/>
      })}
    </div>
  )
}

export const ConversationIndexStatus = props => {
  const { result, components, onOpen } = props
  const meta = []
  if (result.state) meta.push(result.state)
  if (Number.isFinite(result.message_count)) meta.push(`${result.message_count} messages`)
  // The status root is virtual (Cloud virtualConversationRootRef): it has no
  // summary text of its own, so the cleaner falls back to its title,
  // 'conversation'. Showing that as the summary is noise — drop it.
  let root = result.root
  if (root && root.content === 'conversation') {
    root = { ...root, content: '' }
  }
  return (
    <div className='convIndexStatus'>
      <div className='convIndexStatusState'>
        Conversation index{meta.length ? `: ${meta.join(' · ')}` : ''}
      </div>
      {result.error_message && <div className='convIndexStatusError'>{result.error_message}</div>}
      {root && <ConversationSummaryNode node={root} components={components} onOpen={onOpen}/>}
    </div>
  )
}

const ConversationErrorResult = props => (
  <div className='convIndexStatusError'>
    {props.result.error}{props.result.code ? ` (${props.result.code})` : ''}
  </div>
)

// Dispatcher for every Conversation toolset output. toolName is the
// normalized function name; args the parsed tool-call arguments.
export const ConversationToolResultView = props => {
  const { toolName, args, result, components, onOpen, onMore, renderLeaf } = props
  if (!result) return null
  if (result.error) return <ConversationErrorResult result={result}/>
  const name = String(toolName || '')
  if (name.endsWith('search') || Array.isArray(result.hits)) {
    return (
      <ConversationSearchResults
        results={result.hits}
        query={(args && args.query) || result.query}
        conversationId={result.conversation_id}
        onOpen={onOpen}
        nextStartAt={result.next_start_at}
        onMore={onMore}
      />
    )
  }
  if (name.endsWith('browse') || Array.isArray(result.children)) {
    return <ConversationBrowseResult result={result} components={components} onOpen={onOpen} onMore={onMore} renderLeaf={renderLeaf}/>
  }
  if (name.endsWith('open_link') || result.node) {
    return <ConversationOpenLinkResult result={result} components={components} onOpen={onOpen} onMore={onMore} renderLeaf={renderLeaf}/>
  }
  if (name.endsWith('status') || result.state) {
    return <ConversationIndexStatus result={result} components={components} onOpen={onOpen}/>
  }
  // Anything future-shaped.
  return (
    <pre className='convToolResultFallback'>{JSON.stringify(result, null, 2)}</pre>
  )
}
